import { useCallback, useEffect, useMemo, useRef, type FC } from "react";
import { StepSection } from "@components/StepSection";
import { StepTitleDescription } from "@/components/StepTitleDescritpion";
import styles from "./ManualInputStep.module.css";
import { useTranslation } from "@/i18n";
import { useForm, useWatch } from "react-hook-form";
import { useActionsModule, useModalManager, useWorkflow } from "@/hooks";
import Button from "@/components/UI/Button/Button";
import { InfoIcon } from "@/components/Icons";
import { HowToReadModal } from "@/components/HowToReadModal";
import {
  useConfigStore,
  useContactLensesStore,
  useStepStore,
  useStoreActions,
} from "@/store";
import { GlobalPrescriptionTable } from "@/components/GlobalPrescriptionTable/GlobalPrescriptionTable";
import { LensPrescription } from "@/types/prescription.types";
import { ExitModal } from "@/components/ExitModal";
import { buildLensesDataToSend, formatCartMode } from "@/utils";

interface ManualInputStepProps {
  title: string;
  description?: string;
  onComplete?: () => void;
}

export const ManualInputStep: FC<ManualInputStepProps> = ({
  title,
  description,
}) => {
  const { t } = useTranslation();
  const howToReadModal = useModalManager("how-to-read-prescription");
  const confirmationExitModal = useModalManager("confirmation-exit");
  const { handleNext, canGoNext } = useWorkflow();

  const { config } = useConfigStore();

  const mainStoreActions = useStoreActions();

  const { steps } = useStepStore();

  const { contactLensesData, actions } = useContactLensesStore();
  const {
    removeProduct,
    genericAddToCart,
    genericSaveEditFromCart,
    goBackToPLP,
  } = useActionsModule();

  // whenever the ContactLensesData state changes, recompute step visibility
  useEffect(() => {
    if (config) {
      mainStoreActions.generateSteps({
        ...config,
        contactLensesData: contactLensesData,
      });
    }
  }, [contactLensesData]);

  const defaultvalues = useMemo(() => {
    let defaultValues: { [key: string]: string } = {};
    if (contactLensesData?.OD?.prescription) {
      const prescriptionKeys: (keyof LensPrescription)[] = Object.keys(
        contactLensesData?.OD?.prescription,
      ) as (keyof LensPrescription)[];
      prescriptionKeys.forEach((key) => {
        defaultValues[`OD_${key}`] =
          contactLensesData?.OD?.prescription?.[key] ?? "";
      });
    }
    if (contactLensesData?.OS?.prescription) {
      const prescriptionKeys: (keyof LensPrescription)[] = Object.keys(
        contactLensesData?.OS?.prescription,
      ) as (keyof LensPrescription)[];
      prescriptionKeys.forEach((key) => {
        defaultValues[`OS_${key}`] =
          contactLensesData?.OS?.prescription?.[key] ?? "";
      });
    }

    return defaultValues;
  }, [contactLensesData]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    register,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: defaultvalues,
  });
  const samePrescription = watch("samePrescription");

  const ODAttributes = Object.keys(
    contactLensesData?.OD?.product?.attributes || {},
  );

  const ODFormValues = useWatch({
    control,
    name: ODAttributes.map((attr) => `OD_${attr}`),
  });

  const isCopyingRef = useRef(false);

  useEffect(() => {
    if (isCopyingRef.current) return;

    if (samePrescription && contactLensesData?.OD?.product?.attributes) {
      isCopyingRef.current = true;

      ODAttributes.forEach((key) => {
        clearErrors(`OS_${key}`);
      });

      ODAttributes.forEach((key) => {
        setValue(`OS_${key}`, watch(`OD_${key}`), {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      });

      setTimeout(() => {
        isCopyingRef.current = false;
      }, 0);
    }
  }, [samePrescription, ODFormValues, setValue, clearErrors]);

  const onSubmit = (data: any) => {
    const prescriptionOD: { [key: string]: string } = {};
    const prescriptionOS: { [key: string]: string } = {};
    Object.keys(data).forEach((key) => {
      if (key.startsWith("OD_")) {
        prescriptionOD[key.slice(3)] = data[key];
      } else if (key.startsWith("OS_")) {
        prescriptionOS[key.slice(3)] = data[key];
      }
    });

    // Merge fixed values (BC, DIA, etc. from attributes with a single value)
    // into the form data so nothing gets lost
    Object.entries(contactLensesData?.OD?.product?.attributes || {}).forEach(
      ([key, value]) => {
        if (Array.isArray(value) && value.length === 1 && value[0]) {
          prescriptionOD[key] = value[0];
        }
      },
    );

    Object.entries(contactLensesData?.OS?.product?.attributes || {}).forEach(
      ([key, value]) => {
        if (Array.isArray(value) && value.length === 1 && value[0]) {
          prescriptionOS[key] = value[0];
        }
      },
    );

    // Also merge any prescription values already stored (e.g. from previous steps)
    const mergedOD: LensPrescription = {
      ...(contactLensesData?.OD?.prescription ?? {}),
      ...prescriptionOD,
    };
    const mergedOS: LensPrescription = {
      ...(contactLensesData?.OS?.prescription ?? {}),
      ...prescriptionOS,
    };

    actions.updatePrescriptions({ OD: mergedOD, OS: mergedOS });

    // controls that if a product exist and is connected to an order it means we are editing
    const fromCart =
      ((contactLensesData?.OD && contactLensesData.OD.product?.orderItemId) ||
        !contactLensesData?.OD) &&
      ((contactLensesData?.OS && contactLensesData.OS.product?.orderItemId) ||
        !contactLensesData?.OS) &&
      config?.orderId;

    const cartmode = formatCartMode(config);

    if (canGoNext) {
      handleNext();
    } else {
      const lensDataToSend = buildLensesDataToSend(
        contactLensesData,
        undefined,
        mergedOD,
        mergedOS,
      );
      try {
        mainStoreActions.setWidgetBlocked(true);
        if (fromCart) {
          mainStoreActions.setLoading(true);
          genericSaveEditFromCart(lensDataToSend, cartmode);
          // todo: verificare se devo chiudere il widget manualmente nel caso in cui si apre direttamente da carrello senza navigare su nessun prodotto
        } else {
          mainStoreActions.setLoading(true);
          genericAddToCart(lensDataToSend);
        }
      } catch (error) {
        console.error("Error during add to cart:", error);
        mainStoreActions.setLoading(false);
      }
    }
  };

  const handleRemoveLens = (eye: "OD" | "OS") => {
    const editFromCart =
      contactLensesData?.OD?.product?.orderItemId &&
      contactLensesData?.OS?.product?.orderItemId;
    if (
      editFromCart &&
      config &&
      config?.orderId &&
      contactLensesData?.[eye]?.product?.orderItemId
    ) {
      // edit from cart so remove item
      removeProduct(
        config && config?.orderId,
        contactLensesData?.[eye]?.product?.orderItemId,
        true,
        eye,
      );

      console.log("remove product from cart");
    } else {
      // check if i'm deleting the the item already in cart
      if (contactLensesData?.[eye]?.product?.orderItemId) {
        // if so disconnect item from orderitemID
        console.log("remove product connection from other product");
        removeProduct(
          config?.orderId || "",
          contactLensesData?.[eye]?.product?.orderItemId,
          false,
          eye,
        );
        switch (eye) {
          case "OD":
            actions.removeOD();
            break;
          case "OS":
            actions.removeOS();
            break;
        }
        // step generation will be triggered by the effect watching contactLensesData
      } else {
        // otherwise exit
        handleRemoveProductModal();
      }
    }
  };

  const handleRemoveProductModal = () => {
    confirmationExitModal.open(
      <ExitModal
        onCancel={() => confirmationExitModal.close()}
        onConfirm={() => {
          confirmationExitModal.close();
          if (config) {
            const cartMode = {
              orderId: config?.orderId,
              OD: config?.contactLensesData?.OD?.product?.orderItemId
                ? {
                    orderProductId:
                      config.contactLensesData.OD.product.orderItemId,
                  }
                : undefined,
              OS: config?.contactLensesData?.OS?.product?.orderItemId
                ? {
                    orderProductId:
                      config.contactLensesData.OS.product.orderItemId,
                  }
                : undefined,
            };
            goBackToPLP(null, cartMode, config);
          }
          // todo: è giusto che qua venga chiuso così
        }}
        title={t("steps.advancedPrescription.removeProductTitle")}
        description={t("steps.advancedPrescription.removeProductDescription")}
        exitConfirmText={t(
          "steps.advancedPrescription.removeProductConfirmText",
        )}
        exitCancelText={t("steps.advancedPrescription.removeProductCancelText")}
      />,
      "auto",
    );
  };

  const handleOpenHowToReadModal = () => {
    console.log("Open 'How to read your prescription' modal");
    howToReadModal.open(
      <HowToReadModal
        watch={watch}
        setValue={setValue}
        control={control}
        register={register}
        onClose={() => howToReadModal.close()}
      />,
      "auto",
    );
  };

  const afterLogin = useCallback(
    (success: boolean) => {
      if (success && config) {
        mainStoreActions.resetState();
        mainStoreActions.initializeStore(config);
        mainStoreActions.generateSteps({
          ...config,
          contactLensesData: contactLensesData,
        });
      }
    },
    [config, steps],
  );

  const handleLogin = () => {
    config?.loginModule.login(afterLogin);
  };

  return (
    <StepSection>
      <div className={styles.stepTitleContainer}>
        <StepTitleDescription
          title={title}
          description={description}
          loginDescription={t("steps.prescriptionType.loginDescription")}
          loginButtonText={t("steps.prescriptionType.loginButtonText")}
          loginAvailable={!config?.loginModule.isLoggedIn()}
          login={handleLogin}
        />
        <Button
          variant="link"
          onClick={handleOpenHowToReadModal}
          icon={<InfoIcon color="text" size={16} />}
          iconPosition="before"
        >
          {t("steps.prescriptionType.howToReadYourPrescription")}
        </Button>
      </div>
      <form
        id="prescription-form"
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <GlobalPrescriptionTable
          contactLensesData={contactLensesData}
          control={control}
          register={register}
          samePrescription={Boolean(samePrescription)}
          handleRemoveLens={handleRemoveLens}
        />
        {Object.entries(errors).length > 0 && (
          <label className={styles.errorMessage}>
            {Object.entries(errors).length > 1
              ? t("steps.advancedPrescription.errors")
              : t("steps.advancedPrescription.error")}
          </label>
        )}
      </form>
    </StepSection>
  );
};
